import api from './api.service';

function unwrap<T>(response: { data?: { data?: T } }): T {
  return (response?.data?.data ?? response?.data) as T;
}

export interface EligibleQuotationRow {
  id: string;
  quotation_number: string;
  revision_number: number;
  quotation_date: string;
  valid_until?: string;
  project_name?: string;
  grand_total: number;
  status: string;
  converted_value: number;
  remaining_value: number;
  source_type: string;
  currency: string;
}

export interface ConsolidationLineRow {
  quotation_id: string;
  quotation_number: string;
  quotation_item_id: string;
  line_number: number;
  item_code?: string;
  item_description: string;
  uom?: string;
  unit_price: number;
  original_qty: number;
  converted_qty: number;
  remaining_qty: number;
  line_net_amount: number;
}

export interface ConsolidationPreviewResult {
  quotations: Array<{ id: string; quotation_number: string; grand_total: number }>;
  selected_lines: Array<{
    quotation_number: string;
    quotation_item_id: string;
    item_code: string;
    item_description: string;
    award_qty: number;
    line_value: number;
  }>;
  total_value: number;
  warnings: string[];
}

export interface ConsolidationCreatePayload {
  customer_id: string;
  quotation_ids: string[];
  lines: Array<{ quotation_item_id: string; award_qty: number }>;
  customer_po_number?: string;
  customer_po_date?: string;
  delivery_terms?: string;
  payment_terms?: string;
  remarks?: string;
}

export const quotationConsolidationService = {
  async listEligibleQuotations(customerId: string): Promise<EligibleQuotationRow[]> {
    const res = await api.get(`/sales/quotation-consolidation/customers/${customerId}/quotations`);
    return unwrap(res);
  },

  async loadLines(quotationIds: string[]): Promise<ConsolidationLineRow[]> {
    const res = await api.post('/sales/quotation-consolidation/lines', { quotation_ids: quotationIds });
    return unwrap(res);
  },

  async preview(payload: ConsolidationCreatePayload): Promise<ConsolidationPreviewResult> {
    const res = await api.post('/sales/quotation-consolidation/preview', payload);
    return unwrap(res);
  },

  async create(payload: ConsolidationCreatePayload) {
    const res = await api.post('/sales/quotation-consolidation/create', payload);
    return unwrap<{ id: string; sales_order_number: string }>(res);
  },

  async getLinksBySalesOrder(salesOrderId: string) {
    const res = await api.get(`/sales/quotation-consolidation/sales-orders/${salesOrderId}/links`);
    return unwrap(res);
  },

  async getLinksByQuotation(quotationId: string) {
    const res = await api.get(`/sales/quotation-consolidation/quotations/${quotationId}/links`);
    return unwrap(res);
  },

  async getConversionRegister() {
    const res = await api.get('/sales/quotation-consolidation/reports/register');
    return unwrap(res);
  },

  async getQuotationsByStatus(status: string) {
    const res = await api.get('/sales/quotation-consolidation/reports/quotations', {
      params: { status },
    });
    return unwrap(res);
  },
};
